import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Icon,
	IconButton,
	MenuItem,
	Typography,
	Checkbox,
	FormControlLabel,
	Card,
	CardHeader,
	CardContent,
	Box,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Select,
	FormControl,
	InputLabel,
	TextField,
	CircularProgress
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { useForm } from '@fuse/hooks';
import { SelectFormsy } from '@fuse';
import Formsy from 'formsy-react';

import * as UserActions from '../../auth/store/actions/';
import * as ActionsDialog from 'app/store/actions';
import { getLdapServers } from 'app/main/settings/store/actions/ldap.actions';
import { getOpenIdConfigs } from 'app/main/settings/store/actions/oidc.actions';
import {
  addLdapCollaborator,
  removeLdapCollaborator,
  addOpenidcCollaborator,
  removeOpenidcCollaborator,
  addOrgCollaborator as addClusterOrgCollaborator,
  removeOrgCollaborator as removeClusterOrgCollaborator,
  getClusterCapacitySharing,
  updateClusterCapacitySharing
} from 'app/main/cluster/store/actions/cluster.actions';
import {
  addOrgCollaborator as addCredentialOrgCollaborator,
  removeOrgCollaborator as removeCredentialOrgCollaborator
} from 'app/main/credentials/store/actions/credential.actions';
import { getOrganizations, getMyOrganization } from 'app/main/organizations/store/actions/organization.actions';
import { USER_AUTH_TYPE, USER_ROLE } from 'app/main/util/enums';

const useStyles = makeStyles((theme) => ({
  table: {
    '& th': {
      padding: '4px',
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
    },
    '& td': {
      paddingBottom: '2px'
    }
  },
  card: {
    marginBottom: theme.spacing(3),
    boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)',
    borderRadius: '8px',
    overflow: 'visible'
  },
  cardHeader: {
    paddingBottom: theme.spacing(1),
    backgroundColor: '#f7f9fc'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#0f2147'
  },
  dialogContent: {
    padding: '0px',
    margin: '10px 0px 0px 20px',
    width: '95%',
    maxWidth: '100%',
    overflow: 'auto',
    maxHeight: 'calc(100vh - 120px)', // Adjust based on header height
    flex: 1 // Take remaining space
  },
  addSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  addRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%'
  },
  selectWrapper: {
    flex: 1,
    minWidth: '250px'
  },
  typeSelector: {
    minWidth: '150px',
    maxWidth: '180px'
  },
  mainContainer: {
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden' // Hide the global scrollbar
  },
  modalHeader: {
    backgroundColor: '#1e293b',
    color: 'white',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8
  },
  nameColumn: {
    width: '35%',
    minWidth: '120px'
  },
  typeColumn: {
    width: '25%',
    minWidth: '100px'
  },
  sudoColumn: {
    width: '25%',
    minWidth: '90px',
    textAlign: 'center'
  },
  actionsColumn: {
    width: '15%',
    minWidth: '60px',
    textAlign: 'right'
  },
  stickyTableHead: {
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 10
  },
  tableContainer: {
    maxHeight: '400px',
    overflow: 'auto'
  }
}));

const Collaborators = props => {
  const dispatch = useDispatch();
  const classes = useStyles();

  // redux selectors
  const cluster = useSelector(({ clusters }) => clusters.cluster.selected);
  const credential = useSelector(({ credentials }) => credentials.credential.selected);
  const stack = useSelector(({ stacks }) => stacks.stack.selected);
  const username = useSelector(({ auth }) => auth.user.username);
  const userRole = useSelector(({ auth }) => auth.user.role);
  const isSuperAdmin = userRole && userRole.includes('super-admin');
  const userOrgId = useSelector(({ auth }) => auth.user.organizationId);
  const isOrgAdmin = !isSuperAdmin && userRole && userRole.includes('admin') && userOrgId;
  const allUsers = useSelector(({ auth }) => auth.user.users);
  const rawLdap = useSelector(({ settings }) => settings.settings.ldapServers || []);
  const rawOidc = useSelector(({ settings }) => settings.settings.oidcConfigs || []);
  const rawOrgs = useSelector(({ organizations }) => organizations.organization.list || []);
  const myOrganization = useSelector(({ organizations }) => organizations.organization.myOrganization);

  // component state
  const [data, setData] = useState(props.resource.data);
  const [users, setUsers] = useState([]);
  const [ldapServers, setLdapServers] = useState([]);
  const [openidcConfigs, setOpenidcConfigs] = useState([]);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [selectedLdapServer, setSelectedLdapServer] = useState('');
  const [selectedOpenidcConfig, setSelectedOpenidcConfig] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [orgShareRoles, setOrgShareRoles] = useState({ admin: true, user: false, instructor: false });
  const { form, handleChange, resetForm, setForm } = useForm({ collaborator: '' });
  const [id] = useState(props.resource.data.id);
  const [type] = useState(props.resource.type);
  const [grantSudo, setGrantSudo] = useState(false);
  const [collaboratorType, setCollaboratorType] = useState('local'); // 'local', 'ldap', 'oidc', 'org'
  const [deletingUsers, setDeletingUsers] = useState(new Set()); // Track which users are being deleted
  const [isAdding, setIsAdding] = useState(false); // Track if any add operation is in progress
  const [selectedUser, setSelectedUser] = useState(null); // Track selected user object for admin check
  const [confirmRemove, setConfirmRemove] = useState(null); // { type: 'user'|'org', id: string, name: string }

  // Feature: two-orgs-one-tenancy - per-org desktop capacity split, only
  // meaningful once the cluster is actually shared with another org.
  const [capacitySharing, setCapacitySharing] = useState({ mode: 'unrestricted', maxConcurrentDesktops: '', allocations: [] });
  const [activeCounts, setActiveCounts] = useState({});
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [capacitySaving, setCapacitySaving] = useState(false);
  const [capacityError, setCapacityError] = useState('');

  const loadCapacitySharing = React.useCallback(() => {
    if (type !== 'cluster' || !id) return;
    setCapacityLoading(true);
    dispatch(getClusterCapacitySharing(id))
      .then((result) => {
        const cs = result.capacitySharing || { mode: 'unrestricted', maxConcurrentDesktops: '', allocations: [] };
        setCapacitySharing({
          mode: cs.mode || 'unrestricted',
          maxConcurrentDesktops: cs.maxConcurrentDesktops || '',
          allocations: cs.allocations || []
        });
        setActiveCounts(result.activeCountsByOrganization || {});
      })
      .catch(() => {})
      .then(() => setCapacityLoading(false));
  }, [dispatch, type, id]);

  useEffect(() => {
    dispatch(UserActions.getUsers());
    if (isSuperAdmin) {
      dispatch(getOrganizations(false));
    } else if (isOrgAdmin) {
      dispatch(getMyOrganization());
    }
    if (type === 'cluster') {
      dispatch(getLdapServers());
      dispatch(getOpenIdConfigs());
    }
  }, [dispatch, type, isSuperAdmin, isOrgAdmin]);

  useEffect(() => {
    switch (type) {
      case 'cluster':
        // Sync with Redux selected cluster to get latest updates after collaborator operations
        if (cluster && cluster.id === id) {
          setData(cluster);
        } else {
          setData(props.resource.data);
        }
        break;
      case 'stack':
        stack && setData(stack);
        break;
      case 'credential':
        credential && setData(credential);
        break;
      default:
        break;
    }
  }, [props.resource.data, credential, stack, type, cluster, id]);

  useEffect(() => {
    loadCapacitySharing();
  }, [loadCapacitySharing, data && data.sharedOrganizations && data.sharedOrganizations.length]);

  useEffect(() => {
    if (allUsers.length > 0 && data) {
      const filtered = allUsers.filter(u =>
        u.authType === USER_AUTH_TYPE.LOCAL &&
        !data.user.includes(u.name) &&
        (type === 'cluster' || u.admin || (u.role && u.role.includes(USER_ROLE.ADMIN)))
      );
      setUsers(filtered);
    }
  }, [allUsers, data, type]);

  useEffect(() => {
    if (rawLdap.length > 0 && data) {
      const filtered = rawLdap.filter(srv =>
        !data.sharedLdapServers?.some(shared => shared.id === srv.id)
      );
      setLdapServers(filtered);
    }
  }, [rawLdap, data]);

  useEffect(() => {
    if (rawOidc.length > 0 && data) {
      const filtered = rawOidc.filter(cfg =>
        !data.sharedOpenidcConfigs?.some(shared => shared.id === cfg.id)
      );
      setOpenidcConfigs(filtered);
    }
  }, [rawOidc, data]);

  useEffect(() => {
    if (data) {
      let orgsToShow = [];
      if (isSuperAdmin) {
        orgsToShow = rawOrgs || [];
      } else if (isOrgAdmin && myOrganization) {
        orgsToShow = [{ _id: myOrganization._id || myOrganization.id, name: myOrganization.name }];
      }
      const filtered = orgsToShow.filter(org =>
        !data.sharedOrganizations?.some(shared => shared.id === org._id)
      );
      setAvailableOrgs(filtered);
    }
  }, [rawOrgs, myOrganization, data, isSuperAdmin, isOrgAdmin]);

  const addCollaborator = async () => {
    setIsAdding(true);
    try {
      // Check if selected user is admin and always grant sudo to admins
      const isAdmin = selectedUser?.role?.includes(USER_ROLE.ADMIN);
      const shouldGrantSudo = grantSudo || isAdmin;

      await dispatch(props.actions.put({ id, user: form.collaborator, type: 'share', hasSudo: shouldGrantSudo }));

      // Update local state immediately for better UX
      const userToAdd = users.find(u => u.id === form.collaborator);
      if (userToAdd) {
        setData(prevData => {
          const updatedData = { ...prevData };

          // Add user to the user array if not already present
          if (!updatedData.user.includes(userToAdd.name)) {
            updatedData.user = [...updatedData.user, userToAdd.name];
          }

          // Add sudo permission if granted or user is admin
          if (shouldGrantSudo) {
            updatedData.userSudoPermissions = {
              ...updatedData.userSudoPermissions,
              [userToAdd.name]: true
            };
          }

          return updatedData;
        });
      }

      resetForm();
      setGrantSudo(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error adding collaborator:', error);
    } finally {
      setIsAdding(false);
    }
  };
  const confirmAndDeleteCollaborator = (user) => {
    const userObj = allUsers.find(u => u.name === user);
    const displayName = userObj ? `${userObj.firstName} ${userObj.lastName} (${user})` : user;
    setConfirmRemove({ type: 'user', id: user, name: displayName });
  };

  const deleteCollaborator = async (user) => {
    try {
      // Add user to deleting set to show spinner
      setDeletingUsers(prev => new Set([...prev, user]));

      // Dispatch the Redux action and wait for it to complete
      await dispatch(props.actions.put({ id, user, type: 'unshare' }));
      
      // Remove user from deleting set after successful deletion
      setDeletingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(user);
        return newSet;
      });
      
      // Don't update local state manually - let Redux handle it
      // The editCollaborators action already updates the store with the latest data
      
    } catch (error) {
      console.error('Error removing collaborator:', error);
      
      // Remove user from deleting set on error
      setDeletingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(user);
        return newSet;
      });
      
      // Show error notification
      dispatch(ActionsDialog.showMessage({
        message: `Failed to remove collaborator: ${error.message || 'Unknown error'}`,
        variant: 'error'
      }));
    }
  };
  const deleteLdapServer = async serverId => {
    setDeletingUsers(prev => new Set(prev).add(serverId));
    try {
      await dispatch(removeLdapCollaborator({ id, serverId }));
      // Update local state to reflect the removal
      setData(prevData => ({
        ...prevData,
        sharedLdapServers: prevData.sharedLdapServers?.filter(server => server.id !== serverId) || []
      }));
    } catch (error) {
      console.error('Failed to delete LDAP server:', error);
    } finally {
      setDeletingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(serverId);
        return newSet;
      });
    }
  };
  const addLDAPCollaborator = async () => {
    if (!selectedLdapServer) return;
    setIsAdding(true);
    try {
      await dispatch(addLdapCollaborator({ id, serverId: selectedLdapServer }));
      setSelectedLdapServer('');
    } catch (error) {
      console.error('Error adding LDAP collaborator:', error);
    } finally {
      setIsAdding(false);
    }
  };
  const deleteOpenidcCollaborator = async openidcConfigId => {
    setDeletingUsers(prev => new Set(prev).add(openidcConfigId));
    try {
      await dispatch(removeOpenidcCollaborator({ id, openidcConfigId }));
      // Update local state to reflect the removal
      setData(prevData => ({
        ...prevData,
        sharedOpenidcConfigs: prevData.sharedOpenidcConfigs?.filter(config => config.id !== openidcConfigId) || []
      }));
    } catch (error) {
      console.error('Failed to delete OIDC config:', error);
    } finally {
      setDeletingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(openidcConfigId);
        return newSet;
      });
    }
  };
  const addOpenidcCollaboratorToCluster = async () => {
    if (!selectedOpenidcConfig) return;
    setIsAdding(true);
    try {
      await dispatch(addOpenidcCollaborator({ id, openidcConfigId: selectedOpenidcConfig }));
      setSelectedOpenidcConfig('');
    } catch (error) {
      console.error('Error adding OIDC collaborator:', error);
    } finally {
      setIsAdding(false);
    }
  };
  const addOrgCollaborator = async () => {
    if (!selectedOrg) return;
    const roles = Object.keys(orgShareRoles).filter(r => orgShareRoles[r]);
    if (roles.length === 0) return;
    setIsAdding(true);
    try {
      const addAction = type === 'cluster' ? addClusterOrgCollaborator : addCredentialOrgCollaborator;
      await dispatch(addAction({ id, organizationId: selectedOrg, roles }));
      setSelectedOrg('');
      setOrgShareRoles({ admin: true, user: false, instructor: false });
    } catch (error) {
      console.error('Error adding org collaborator:', error);
    } finally {
      setIsAdding(false);
    }
  };
  const confirmAndDeleteOrgCollaborator = (organizationId) => {
    const orgEntry = data.sharedOrganizations?.find(o => o.id === organizationId);
    const orgName = orgEntry ? orgEntry.label : 'this organization';
    setConfirmRemove({ type: 'org', id: organizationId, name: orgName });
  };

  const deleteOrgCollaborator = async (organizationId) => {
    setDeletingUsers(prev => new Set(prev).add(organizationId));
    try {
      const removeAction = type === 'cluster' ? removeClusterOrgCollaborator : removeCredentialOrgCollaborator;
      const result = await dispatch(removeAction({ id, organizationId }));
      // Update from Redux response which has the updated resource with users removed
      if (result) {
        setData(result);
      }
    } catch (error) {
      console.error('Failed to remove organization:', error);
    } finally {
      setDeletingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(organizationId);
        return newSet;
      });
    }
  };

  // Feature: two-orgs-one-tenancy
  const capacityOrgs = React.useMemo(() => {
    const owner = data && data.organizationId
      ? [{ id: data.organizationId, label: data.organizationName || 'Owner organization' }]
      : [];
    const shared = (data && data.sharedOrganizations) || [];
    return owner.concat(shared.map(o => ({ id: o.id, label: o.label })));
  }, [data]);

  const setCapacityMode = (mode) => setCapacitySharing(prev => ({ ...prev, mode }));
  const setCapacityMax = (maxConcurrentDesktops) => setCapacitySharing(prev => ({ ...prev, maxConcurrentDesktops }));
  const setCapacityPercent = (organizationId, percent) => {
    setCapacitySharing(prev => {
      const allocations = capacityOrgs.map(o => {
        const existing = prev.allocations.find(a => a.organizationId === o.id);
        return {
          organizationId: o.id,
          label: o.label,
          percent: o.id === organizationId ? percent : (existing ? existing.percent : '')
        };
      });
      return { ...prev, allocations };
    });
  };

  const saveCapacitySharing = async () => {
    setCapacitySaving(true);
    setCapacityError('');
    try {
      const body = capacitySharing.mode === 'unrestricted'
        ? { mode: 'unrestricted' }
        : {
          mode: 'split',
          maxConcurrentDesktops: Number(capacitySharing.maxConcurrentDesktops),
          allocations: capacitySharing.allocations
            .filter(a => a.percent !== '' && a.percent !== null && a.percent !== undefined)
            .map(a => ({ organizationId: a.organizationId, label: a.label, percent: Number(a.percent) }))
        };
      await dispatch(updateClusterCapacitySharing(id, body));
      loadCapacitySharing();
    } catch (error) {
      setCapacityError((error && error.data && error.data.message) || 'Failed to save the capacity split.');
    } finally {
      setCapacitySaving(false);
    }
  };

  return (
    <div className={classes.mainContainer}>
      {/* Fixed Header */}
      <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')} style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
        <div className="flex items-center justify-between w-full">
          <Typography variant="h6" style={{ color: 'white', fontWeight: 600 }}>
            {`Collaborators: ${data.name}`}
          </Typography>
          <Button
            className="whitespace-no-wrap"
            id="collaborators_close-btn"
            variant="contained"
            onClick={() => dispatch(ActionsDialog.closeDialog())}
          >
            CLOSE
          </Button>
        </div>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {data ? (
          <div>
            {/* Current Collaborators Card */}
            <Card className={classes.card}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center">
                    <Icon style={{marginRight: '8px', color: '#2196F3'}}>people</Icon>
                    <Typography className={classes.sectionTitle}>Current Collaborators</Typography>
                  </Box>
                }
                className={classes.cardHeader}
              />
              <CardContent>
                <div className={classes.tableContainer}>
                  <Table stickyHeader>
                    <TableHead className={classes.stickyTableHead}>
                      <TableRow>
                        <TableCell className={classes.nameColumn}><strong>Name</strong></TableCell>
                        <TableCell className={classes.typeColumn}><strong>Type</strong></TableCell>
                        {type === 'cluster' && <TableCell className={classes.sudoColumn}><strong>Sudo Access</strong></TableCell>}
                        <TableCell className={classes.actionsColumn} align="right"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                  <TableBody>
                    {/* Local Users */}
                    {data.user
                      .filter(user => {
                        const userObj = allUsers.find(u => u.name === user);
                        return (
                          !userObj ||
                          (userObj.authType !== USER_AUTH_TYPE.LDAP &&
                            userObj.authType !== USER_AUTH_TYPE.OPENID_CONNECT)
                        );
                      })
                      .map((user, i) => {
                        const hasSudo = data.userSudoPermissions && data.userSudoPermissions[user];
                        const userObj = allUsers.find(u => u.name === user);
                        const isAdmin = userObj?.role?.includes(USER_ROLE.ADMIN);
                        const displayName = userObj ? `${userObj.firstName} ${userObj.lastName} (${userObj.name})` : `${user.charAt(0).toUpperCase()}${user.slice(1)}`;
                        return (
                          <TableRow id={`user-${i}`} key={i}>
                            <TableCell className={classes.nameColumn}>{displayName}</TableCell>
                            <TableCell className={classes.typeColumn}>Local User</TableCell>
                            {type === 'cluster' && (
                              <TableCell className={classes.sudoColumn}>
                                <Checkbox
                                  checked={hasSudo || isAdmin || false}
                                  disabled={true}
                                  title={isAdmin ? "Admin users automatically have sudo access" : "Sudo permissions can only be set when adding a collaborator"}
                                  onChange={async (e) => {
                                    const hasPermission = e.target.checked;
                                    try {
                                      await dispatch(props.actions.put({
                                        id,
                                        user,
                                        type: 'update-sudo',
                                        hasSudo: hasPermission
                                      }));
                                      // Update local state to reflect the change immediately
                                      setData(prevData => ({
                                        ...prevData,
                                        userSudoPermissions: {
                                          ...prevData.userSudoPermissions,
                                          [user]: hasPermission
                                        }
                                      }));
                                    } catch (error) {
                                      console.error('Failed to update sudo permission:', error);
                                    }
                                  }}
                                  color="primary"
                                />
                              </TableCell>
                            )}
                            <TableCell className={classes.actionsColumn} align="right">
                              <IconButton
                                disabled={
                                  user === data.user[0] ||
                                  deletingUsers.has(user)
                                }
                                id={`${user}-del`}
                                className="cursor-pointer p-0"
                                onClick={() => confirmAndDeleteCollaborator(user)}
                              >
                                {deletingUsers.has(user) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Icon>delete</Icon>
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    
                    {/* LDAP Users */}
                    {data.user
                      .filter(user => {
                        const userObj = allUsers.find(u => u.name === user);
                        return userObj && userObj.authType === USER_AUTH_TYPE.LDAP;
                      })
                      .map((user, i) => {
                        const hasSudo = data.userSudoPermissions && data.userSudoPermissions[user];
                        const userObj = allUsers.find(u => u.name === user);
                        const isAdmin = userObj?.role?.includes(USER_ROLE.ADMIN);
                        const displayName = userObj ? `${userObj.firstName} ${userObj.lastName} (${userObj.name})` : `${user.charAt(0).toUpperCase()}${user.slice(1)}`;
                        return (
                          <TableRow id={`ldap-user-${i}`} key={`ldap-user-${i}`}>
                            <TableCell className={classes.nameColumn}>{displayName}</TableCell>
                            <TableCell className={classes.typeColumn}>LDAP User</TableCell>
                            {type === 'cluster' && (
                              <TableCell className={classes.sudoColumn}>
                                <Checkbox
                                  checked={hasSudo || isAdmin || false}
                                  disabled={true}
                                  title={isAdmin ? "Admin users automatically have sudo access" : "Sudo permissions can only be set when adding a collaborator"}
                                  onChange={async (e) => {
                                    const hasPermission = e.target.checked;
                                    try {
                                      await dispatch(props.actions.put({
                                        id,
                                        user,
                                        type: 'update-sudo',
                                        hasSudo: hasPermission
                                      }));
                                      setData(prevData => ({
                                        ...prevData,
                                        userSudoPermissions: {
                                          ...prevData.userSudoPermissions,
                                          [user]: hasPermission
                                        }
                                      }));
                                    } catch (error) {
                                      console.error('Failed to update sudo permission:', error);
                                    }
                                  }}
                                  color="primary"
                                />
                              </TableCell>
                            )}
                            <TableCell className={classes.actionsColumn} align="right">
                              <IconButton
                                id={`${user}-del`}
                                className="cursor-pointer p-0"
                                onClick={() => confirmAndDeleteCollaborator(user)}
                              >
                                <Icon>delete</Icon>
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    
                    {/* OIDC Users */}
                    {data.user
                      .filter(user => {
                        const userObj = allUsers.find(u => u.name === user);
                        return userObj && userObj.authType === USER_AUTH_TYPE.OPENID_CONNECT;
                      })
                      .map((user, i) => {
                        const hasSudo = data.userSudoPermissions && data.userSudoPermissions[user];
                        const userObj = allUsers.find(u => u.name === user);
                        const isAdmin = userObj?.role?.includes(USER_ROLE.ADMIN);
                        const displayName = userObj ? `${userObj.firstName} ${userObj.lastName} (${userObj.name})` : `${user.charAt(0).toUpperCase()}${user.slice(1)}`;
                        return (
                          <TableRow id={`oidc-user-${i}`} key={`oidc-user-${i}`}>
                            <TableCell className={classes.nameColumn}>{displayName}</TableCell>
                            <TableCell className={classes.typeColumn}>OIDC User</TableCell>
                            {type === 'cluster' && (
                              <TableCell className={classes.sudoColumn}>
                                <Checkbox
                                  checked={hasSudo || isAdmin || false}
                                  disabled={true}
                                  title={isAdmin ? "Admin users automatically have sudo access" : "Sudo permissions can only be set when adding a collaborator"}
                                  onChange={async (e) => {
                                    const hasPermission = e.target.checked;
                                    try {
                                      await dispatch(props.actions.put({
                                        id,
                                        user,
                                        type: 'update-sudo',
                                        hasSudo: hasPermission
                                      }));
                                      setData(prevData => ({
                                        ...prevData,
                                        userSudoPermissions: {
                                          ...prevData.userSudoPermissions,
                                          [user]: hasPermission
                                        }
                                      }));
                                    } catch (error) {
                                      console.error('Failed to update sudo permission:', error);
                                    }
                                  }}
                                  color="primary"
                                />
                              </TableCell>
                            )}
                            <TableCell className={classes.actionsColumn} align="right">
                              <IconButton
                                disabled={deletingUsers.has(user)}
                                id={`${user}-del`}
                                className="cursor-pointer p-0"
                                onClick={() => confirmAndDeleteCollaborator(user)}
                              >
                                {deletingUsers.has(user) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Icon>delete</Icon>
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    
                    {/* LDAP Servers */}
                    {data.sharedLdapServers?.map((server, i) => (
                      <TableRow id={`ldap-${i}`} key={`ldap-${i}`}>
                        <TableCell className={classes.nameColumn}>{server.label}</TableCell>
                        <TableCell className={classes.typeColumn}>LDAP Server</TableCell>
                        {type === 'cluster' && <TableCell className={classes.sudoColumn}>-</TableCell>}
                        <TableCell className={classes.actionsColumn} align="right">
                          <IconButton
                            disabled={deletingUsers.has(server.id)}
                            id={`${server.id}-del`}
                            className="cursor-pointer p-0"
                            onClick={() => deleteLdapServer(server.id)}
                          >
                            {deletingUsers.has(server.id) ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Icon>delete</Icon>
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* OIDC Configs */}
                    {data.sharedOpenidcConfigs?.map((config, i) => (
                      <TableRow id={`openidc-${i}`} key={`openidc-${i}`}>
                        <TableCell className={classes.nameColumn}>{config.label}</TableCell>
                        <TableCell className={classes.typeColumn}>OIDC Config</TableCell>
                        {type === 'cluster' && <TableCell className={classes.sudoColumn}>-</TableCell>}
                        <TableCell className={classes.actionsColumn} align="right">
                          <IconButton
                            disabled={deletingUsers.has(config.id)}
                            id={`${config.id}-del`}
                            className="cursor-pointer p-0"
                            onClick={() => deleteOpenidcCollaborator(config.id)}
                          >
                            {deletingUsers.has(config.id) ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Icon>delete</Icon>
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Organizations */}
                    {data.sharedOrganizations?.map((org, i) => (
                      <TableRow id={`org-${i}`} key={`org-${i}`}>
                        <TableCell className={classes.nameColumn}>
                          {org.label}
                          {org.roles && org.roles.length > 0 && (
                            <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                              Roles: {org.roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell className={classes.typeColumn}>Organization</TableCell>
                        {type === 'cluster' && <TableCell className={classes.sudoColumn}>-</TableCell>}
                        <TableCell className={classes.actionsColumn} align="right">
                          {(isSuperAdmin || (isOrgAdmin && org.id === userOrgId)) && (
                            <IconButton
                              disabled={deletingUsers.has(org.id)}
                              id={`${org.id}-del`}
                              className="cursor-pointer p-0"
                              onClick={() => confirmAndDeleteOrgCollaborator(org.id)}
                            >
                              {deletingUsers.has(org.id) ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Icon>delete</Icon>
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>

            {/* Capacity Sharing - Feature: two-orgs-one-tenancy. Only meaningful
                once the cluster is actually shared with another organization:
                this is the always-up "classroom" case where a fixed capacity
                must be split rather than first-come first-served. */}
            {type === 'cluster' && (isSuperAdmin || isOrgAdmin) && data.sharedOrganizations?.length > 0 && (
              <Card className={classes.card}>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center">
                      <Icon style={{marginRight: '8px', color: '#2196F3'}}>pie_chart</Icon>
                      <Typography className={classes.sectionTitle}>Capacity Sharing</Typography>
                    </Box>
                  }
                  className={classes.cardHeader}
                />
                <CardContent>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                    This cluster is always-up and shared across organizations. Choose whether
                    concurrent desktop (VNC / DCV / Heidi Desktop) sessions are first-come
                    first-served, or split into a fixed share per organization.
                  </Typography>

                  {capacityLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      <FormControl variant="outlined" style={{ minWidth: 220, marginBottom: 16 }}>
                        <InputLabel id="capacity-mode-label">Mode</InputLabel>
                        <Select
                          labelId="capacity-mode-label"
                          label="Mode"
                          value={capacitySharing.mode}
                          onChange={(e) => setCapacityMode(e.target.value)}
                        >
                          <MenuItem value="unrestricted">Unrestricted (first-come, first-served)</MenuItem>
                          <MenuItem value="split">Split capacity per organization</MenuItem>
                        </Select>
                      </FormControl>

                      {capacitySharing.mode === 'split' && (
                        <>
                          <TextField
                            type="number"
                            label="Total concurrent desktop sessions"
                            variant="outlined"
                            value={capacitySharing.maxConcurrentDesktops}
                            onChange={(e) => setCapacityMax(e.target.value)}
                            style={{ marginBottom: 16, display: 'block', maxWidth: 320 }}
                            inputProps={{ min: 1 }}
                          />

                          <Table size="small" style={{ maxWidth: 520, marginBottom: 16 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Organization</TableCell>
                                <TableCell>Share</TableCell>
                                <TableCell>In use now</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {capacityOrgs.map((org) => {
                                const allocation = capacitySharing.allocations.find(a => a.organizationId === org.id);
                                return (
                                  <TableRow key={org.id}>
                                    <TableCell>{org.label}</TableCell>
                                    <TableCell>
                                      <TextField
                                        type="number"
                                        variant="outlined"
                                        size="small"
                                        value={allocation ? allocation.percent : ''}
                                        onChange={(e) => setCapacityPercent(org.id, e.target.value)}
                                        InputProps={{ endAdornment: '%' }}
                                        inputProps={{ min: 0, max: 100 }}
                                        style={{ width: 110 }}
                                      />
                                    </TableCell>
                                    <TableCell>{activeCounts[org.id] || 0}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </>
                      )}

                      {capacityError && (
                        <Typography variant="body2" color="error" style={{ marginBottom: 12 }}>
                          {capacityError}
                        </Typography>
                      )}

                      <Button
                        variant="contained"
                        color="primary"
                        disabled={capacitySaving}
                        onClick={saveCapacitySharing}
                      >
                        {capacitySaving ? <CircularProgress size={18} /> : 'Save Capacity Sharing'}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Add Collaborators Card - Consolidated */}
            <Card className={classes.card}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center">
                    <Icon style={{marginRight: '8px', color: '#4CAF50'}}>person_add</Icon>
                    <Typography className={classes.sectionTitle}>Add Collaborators</Typography>
                  </Box>
                }
                className={classes.cardHeader}
              />
              <CardContent>
                <div className={classes.addSection}>
                  {/* First Row: Type selector and User/Server selector with Add button */}
                  <div className={classes.addRow}>
                    <div className={classes.typeSelector}>
                      <Formsy>
                        <SelectFormsy
                          id="collaborators_type_selector"
                          name="collaboratorType"
                          label="Type"
                          variant="outlined"
                          value={collaboratorType}
                          onChange={(e) => setCollaboratorType(e.target.value)}
                          fullWidth
                          required
                        >
                          <MenuItem value="local">Local Users</MenuItem>
                          {type === 'cluster' && ldapServers.length > 0 && (
                            <MenuItem value="ldap">LDAP Servers</MenuItem>
                          )}
                          {type === 'cluster' && openidcConfigs.length > 0 && (
                            <MenuItem value="oidc">OIDC Configs</MenuItem>
                          )}
                          {(isSuperAdmin || isOrgAdmin) && availableOrgs.length > 0 && (
                            <MenuItem value="org">Organizations</MenuItem>
                          )}
                        </SelectFormsy>
                      </Formsy>
                    </div>

                    {/* Dynamic Content Based on Type */}
                    {collaboratorType === 'local' && (
                      <>
                        <div className={classes.selectWrapper}>
                          {users.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                              {allUsers.length === 0 ? 'Loading users…' : 'No users to add...'}
                            </Typography>
                          ) : (
                            <Autocomplete
                              id="collaborators_collaborator"
                              className={classes.selectWrapper}
                              options={users}
                              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.name})`}
                              renderInput={(params) =>
                                <TextField
                                  {...params}
                                  label="Select User"
                                  placeholder="Search and select a user to add as collaborator"
                                  variant="outlined"
                                  fullWidth
                                  required
                                />
                              }
                              onChange={(event, newValue) => {
                                setForm({
                                  ...form,
                                  collaborator: newValue ? newValue.id : ''
                                });
                                setSelectedUser(newValue);
                                // Auto-check sudo for admin users
                                if (newValue?.role?.includes(USER_ROLE.ADMIN)) {
                                  setGrantSudo(true);
                                }
                              }}
                              value={users.find(u => u.id === form.collaborator) || null}
                            />
                          )}
                        </div>
                        {users.length > 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            id="collaborators_add-collaborator-btn"
                            disabled={!form.collaborator || isAdding}
                            onClick={addCollaborator}
                            startIcon={isAdding ? <CircularProgress size={16} /> : <Icon>add</Icon>}
                          >
                            {isAdding ? 'ADDING...' : 'ADD'}
                          </Button>
                        )}
                      </>
                    )}

                    {collaboratorType === 'ldap' && type === 'cluster' && (
                      <>
                        <div className={classes.selectWrapper}>
                          {ldapServers.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                              {rawLdap.length === 0
                                ? 'Loading LDAP servers…'
                                : 'No LDAP servers to add...'}
                            </Typography>
                          ) : (
                            <Formsy>
                              <SelectFormsy
                                id="collaborators_ldap"
                                name="ldapServer"
                                label="LDAP Server"
                                variant="outlined"
                                value={selectedLdapServer}
                                onChange={e => setSelectedLdapServer(e.target.value)}
                                fullWidth
                                required
                                displayEmpty
                                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                              >
                                <MenuItem value="" disabled>
                                  Select User
                                </MenuItem>
                                {ldapServers.map((srv, i) => (
                                  <MenuItem id={srv.id} key={i} value={srv.id}>
                                    {srv.label}
                                  </MenuItem>
                                ))}
                              </SelectFormsy>
                            </Formsy>
                          )}
                        </div>
                        {ldapServers.length > 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            id="collaborators_add-ldap-collaborator-btn"
                            disabled={!selectedLdapServer || isAdding}
                            onClick={addLDAPCollaborator}
                            startIcon={isAdding ? <CircularProgress size={16} /> : <Icon>add</Icon>}
                          >
                            {isAdding ? 'ADDING...' : 'ADD'}
                          </Button>
                        )}
                      </>
                    )}

                    {collaboratorType === 'oidc' && type === 'cluster' && (
                      <>
                        <div className={classes.selectWrapper}>
                          {openidcConfigs.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                              {rawOidc.length === 0
                                ? 'Loading OIDC configs…'
                                : 'No OIDC configs to add...'}
                            </Typography>
                          ) : (
                            <Formsy>
                              <SelectFormsy
                                id="collaborators_openidc"
                                name="openidcConfig"
                                label="OIDC Config"
                                variant="outlined"
                                value={selectedOpenidcConfig}
                                onChange={e => setSelectedOpenidcConfig(e.target.value)}
                                fullWidth
                                required
                                displayEmpty
                                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                              >
                                <MenuItem value="" disabled>
                                  Select User
                                </MenuItem>
                                {openidcConfigs.map((cfg, i) => (
                                  <MenuItem id={cfg.id} key={i} value={cfg.id}>
                                    {cfg.label}
                                  </MenuItem>
                                ))}
                              </SelectFormsy>
                            </Formsy>
                          )}
                        </div>
                        {openidcConfigs.length > 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            id="collaborators_add-oidc-collaborator-btn"
                            disabled={!selectedOpenidcConfig || isAdding}
                            onClick={addOpenidcCollaboratorToCluster}
                            startIcon={isAdding ? <CircularProgress size={16} /> : <Icon>add</Icon>}
                          >
                            {isAdding ? 'ADDING...' : 'ADD'}
                          </Button>
                        )}
                      </>
                    )}

                    {collaboratorType === 'org' && (
                      <>
                        <div className={classes.selectWrapper}>
                          {availableOrgs.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                              No organizations to add...
                            </Typography>
                          ) : (
                            <FormControl variant="outlined" fullWidth style={{ minWidth: 250 }}>
                              <InputLabel>Select Organization</InputLabel>
                              <Select
                                id="collaborators_org"
                                value={selectedOrg}
                                onChange={e => setSelectedOrg(e.target.value)}
                                label="Select Organization"
                                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                              >
                                {availableOrgs.map((org, i) => (
                                  <MenuItem key={i} value={org._id}>
                                    {org.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </div>
                        {availableOrgs.length > 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            id="collaborators_add-org-collaborator-btn"
                            disabled={!selectedOrg || isAdding}
                            onClick={addOrgCollaborator}
                            startIcon={isAdding ? <CircularProgress size={16} /> : <Icon>add</Icon>}
                          >
                            {isAdding ? 'ADDING...' : 'ADD'}
                          </Button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Role selection for organization sharing */}
                  {collaboratorType === 'org' && availableOrgs.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="textSecondary" style={{ marginBottom: 4, display: 'block' }}>
                        Share with users who have these roles:
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={orgShareRoles.admin}
                            onChange={(e) => setOrgShareRoles(prev => ({ ...prev, admin: e.target.checked }))}
                            color="primary"
                            size="small"
                          />
                        }
                        label="Admin"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={orgShareRoles.instructor}
                            onChange={(e) => setOrgShareRoles(prev => ({ ...prev, instructor: e.target.checked }))}
                            color="primary"
                            size="small"
                          />
                        }
                        label="Instructor"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={orgShareRoles.user}
                            onChange={(e) => setOrgShareRoles(prev => ({ ...prev, user: e.target.checked }))}
                            color="primary"
                            size="small"
                          />
                        }
                        label="User"
                      />
                    </Box>
                  )}

                  {/* Second Row: Grant Sudo Access - Only show for local users, not for credentials */}
                  {collaboratorType === 'local' && users.length > 0 && type === 'cluster' && (
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={grantSudo}
                            onChange={(e) => setGrantSudo(e.target.checked)}
                            disabled={selectedUser?.role?.includes(USER_ROLE.ADMIN)}
                            color="primary"
                          />
                        }
                        label="Grant Sudo Access"
                      />
                      {selectedUser?.role?.includes(USER_ROLE.ADMIN) && (
                        <Typography variant="caption" color="textSecondary" style={{ marginLeft: '32px', display: 'block', marginTop: '-8px' }}>
                          This user is an admin and automatically has sudo access
                        </Typography>
                      )}
                    </Box>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Typography variant="h6" align="center" style={{ padding: '40px' }}>
            ...Loading Collaborators
          </Typography>
        )}
      </DialogContent>

      {/* Inline Confirmation Dialog */}
      <Dialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmRemove?.type === 'org' ? 'Remove Organization' : 'Remove Collaborator'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{confirmRemove?.name}</strong>
            {confirmRemove?.type === 'org' ? ` and all its shared users from this ${type}?` : ` from this ${type}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              const { type: removeType, id: removeId } = confirmRemove;
              setConfirmRemove(null);
              if (removeType === 'org') {
                deleteOrgCollaborator(removeId);
              } else {
                deleteCollaborator(removeId);
              }
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};


export default Collaborators;
